import {
  completeLoadMeta,
  createIdleLoadMeta,
  failLoadMeta,
  startLoadMeta,
} from "../../services/firebase/loadState";
import {
  appendSiteMemberTransaction,
  ensureSiteMemberSubscriptionAccess,
  updateLastScorecard,
  writeSiteMemberInfo,
} from "../../services/firebase/firestoreDataLayer";

const siteMembers = {
  namespaced: true,
  state() {
    return {
      //Loaded from database.  Info on siteMember that is not part of Auth data
      lastScoreCard: null,
      expiresDate: null,
      isUserSubscriber: false,
      hasUserEverSubscribed: false,
      loadMeta: createIdleLoadMeta(),
    };
  },

  //Getters for site member data
  getters: {
    //Used to get the last score used by the user
    lastScoreCard(state) {
      if (state.siteMember === undefined) {
        return null;
      }
      return state.siteMember.lastScoreCard;
    },

    //Used to get user subscription status, returns true if user is a subscriber
    isUserSubscriber(state) {
      if (state.siteMember === undefined) {
        return false;
      }
      return state.siteMember.isUserSubscriber;
    },

    subscriptionExpiresOn(state) {
      if (state.siteMember === undefined) {
        return null;
      }
      return state.siteMember.expiresDate;
    },

    hasUserEverSubscribed(state) {
      if (state.siteMember === undefined) {
        return false;
      }
      return state.siteMember.hasUserEverSubscribed;
    },

    loadState(state) {
      return state.loadMeta.loadState;
    },

    loadError(state) {
      return state.loadMeta.error;
    },
  },

  mutations: {
    /* Load site member data to memory should be called after site member data is loaded from database */
    loadSiteMember(state, payLoad) {
      state.siteMember = payLoad;
    },

    siteMemberLoadStarted(state, requestKey) {
      state.loadMeta = startLoadMeta(requestKey);
    },

    siteMemberLoadSucceeded(state, result) {
      state.loadMeta = completeLoadMeta(state.loadMeta, result);
    },

    siteMemberLoadFailed(state, error) {
      state.loadMeta = failLoadMeta(state.loadMeta, error);
    },

    resetSiteMemberState(state) {
      state.lastScoreCard = null;
      state.expiresDate = null;
      state.isUserSubscriber = false;
      state.hasUserEverSubscribed = false;
      state.siteMember = undefined;
      state.loadMeta = createIdleLoadMeta();
    },
  },

  /*  Database reads/writes are in actions. */
  actions: {
    /*Load sitemember (users) data from the db*/
    async loadSiteMember({ commit, dispatch }, userID) {
      commit("siteMemberLoadStarted", `site-member:${userID}`);
      const result = await dispatch("refreshHomeContext", null, { root: true });

      if (result?.error) {
        commit("siteMemberLoadFailed", result.error);
        return;
      }

      const payLoad = result?.data?.siteMember;
      if (payLoad?.exists) {
        commit("loadSiteMember", payLoad);
        commit("siteMemberLoadSucceeded", result);
      } else {
        await dispatch("writeDefaultSiteMemberInfo", {
          userID: userID,
          transaction: [createDefaultTransaction(null)],
        });
      }
    },

    /* update the last score card to the database  */
    async updateLastScoreCard({ dispatch }, payLoad) {
      const result = await updateLastScorecard(
        payLoad.userID,
        payLoad.lastScoreCard
      );
      if (result.error) {
        throw result.error;
      }
      //Reload from the database and update memory
      dispatch("loadSiteMember", payLoad.userID);
    },

    /* Write a membership transaction to the database */
    async writeMembershipTransaction({ dispatch }, payLoad) {
      const result = await appendSiteMemberTransaction(
        payLoad.userID,
        payLoad.transaction
      );
      if (result.error) {
        throw result.error;
      }
      //Reload from the database and update memory
      dispatch("loadSiteMember", payLoad.userID);
    },

    async ensureSubscriberAccess({ dispatch }, payLoad) {
      const result = await ensureSiteMemberSubscriptionAccess({
        userEmail: payLoad.userEmail ?? null,
        userId: payLoad.userID,
        years: payLoad.years ?? 1,
        days: payLoad.days ?? 0,
      });
      if (result.error) {
        throw result.error;
      }

      await dispatch("loadSiteMember", payLoad.userID);

      if (!result.data?.granted) {
        return false;
      }
      return true;
    },

    //Write the default site member info to the database
    async writeDefaultSiteMemberInfo({ dispatch }, payLoad) {
      const result = await writeSiteMemberInfo(payLoad.userID, {
        transaction: payLoad.transaction,
        lastScoreCard: payLoad.lastScoreCard ?? null,
      });
      if (result.error) {
        throw result.error;
      }
      //Reload from the database and update memory
      dispatch("loadSiteMember", payLoad.userID);
    },
  },
};

function createDefaultTransaction(userEmail) {
  let datePurchase = new Date();
  let dateExpires = new Date();
  dateExpires.setDate(dateExpires.getDate() - 1);

  return createTransaction(userEmail, datePurchase, dateExpires, 0);
}

function createTransaction(userEmail, datePurchase, dateExpires, amountPaid) {
  return {
    datePurchase,
    dateExpires,
    userEmail,
    amountPaid,
    ppOrder: { transaction: null },
  };
}

export default siteMembers;
